"use client";

import Link from "next/link";
import { StatusBadge } from "@/components/blocks/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/Card";
import { MapPin, Phone, Mail, Clock, Navigation } from "lucide-react";
import type { CustomerShopSummary } from "@/features/shop/types/customer-shop";
import { buildGoogleMapsUrl } from "../public-site.utils";

interface PublicStoreCardProps {
  shop: CustomerShopSummary;
  productCount?: number;
}

export function PublicStoreCard({ shop, productCount = 0 }: PublicStoreCardProps) {
  const location = [
    shop.location,
    shop.subLocation,
    shop.village,
  ].filter(Boolean).join(", ");
  const region = [shop.ward, shop.subCounty, shop.county].filter(Boolean).join(", ");

  return (
    <Card className="overflow-hidden  bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
      <CardContent className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-semibold ">{shop.name}</h3>
            <p className="mt-1 flex items-start gap-2 text-sm text-muted-foreground">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
              <span>{location || region || "Shop location"}</span>
            </p>
          </div>
          <StatusBadge
            status={shop.isActive ? "ACTIVE" : "INACTIVE"}
            className="rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em]"
          />
        </div>

        <div className="space-y-2 text-sm text-muted-foreground">
          <p className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-emerald-500" />
            <span>Available in marketplace</span>
          </p>
          <p className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-emerald-500" />
            <span>{productCount} products listed</span>
          </p>
          <p className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-emerald-500" />
            <span>Open for fresh orders</span>
          </p>
        </div>

        <div className="flex gap-3">
          <Button asChild className="flex-1 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700">
            <Link href={`/shop/store-list/${shop._id}`}>View Shop</Link>
          </Button>
          <Button
            asChild
            variant="brand"
            className="rounded-xl"
          >
            <a
              href={buildGoogleMapsUrl(`${shop.name} ${location || region}`, shop)}
              target="_blank"
              rel="noreferrer"
            >
              <Navigation className="mr-2 h-4 w-4" />
              Directions
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
